import React, { useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  LogIn,
  ShieldCheck,
  Loader2,
} from "lucide-react";

import { toast } from "react-toastify";
import { http } from "../../../axios/axios";
import { AuthContext } from "../../../context/authContext";

function Login() {

  const { login } = useContext(AuthContext); // Access global login function
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState("credentials");
  const [pendingEmail, setPendingEmail] = useState("");
 
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const onSubmit = async (data) => {
  // console.log("===== LOGIN SUBMIT =====");
  // console.log("Login step:", loginStep);

  if (loginStep === "credentials") {
    // console.log("Email:", data.email);
    // console.log("Password exists:", Boolean(data.password));
  } else {
    // console.log("OTP email:", pendingEmail);
    // console.log("OTP exists:", Boolean(data.otp));
  }

  setIsLoading(true);

  try {
    let response;

    if (loginStep === "credentials") {
      // console.log("Sending request to /auth/login");

      response = await http.post("/auth/login", {
        email: data.email,
        password: data.password,
      });
    } else {
      // console.log("Sending request to /auth/verify-login-otp");

      response = await http.post("/auth/verify-login-otp", {
        email: pendingEmail,
        otp: data.otp,
      });
    }

    // console.log("LOGIN API RESPONSE:", response.data);

    // First step: credentials accepted, OTP required
    if (response.data.requiresOtp) {
      setPendingEmail(response.data.email);
      setLoginStep("otp");

      toast.success(
        "Verification code sent to your admin email"
      );

      return;
    }

    // Second step: OTP verified
    const { user, token } = response.data;

    // console.log("Login successful:", user);

    login(user, token);

    toast.success("🛡️ Suraksha Security Verified");

    navigate("/admin/dashboard");
  } catch (error) {
    console.error("===== LOGIN ERROR =====");
    console.error("Full error:", error);
    console.error("Status:", error.response?.status);
    console.error("Response:", error.response?.data);
    console.error("Request URL:", error.config?.url);
    console.error("Base URL:", error.config?.baseURL);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Login failed";

    setError("root", {
      message: errorMessage,
    });

    toast.error(errorMessage);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&w=1920&q=80')",
      }}
    >
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"></div>

      {/* Login Card */}
      <div className="relative w-full max-w-xl bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl p-8 md:p-12 text-white">
        {/* Branding */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/40 mb-4 border border-blue-400/30 transform -rotate-3">
            <ShieldCheck className="text-white w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold tracking-tight">
            SURAKSHA <span className="text-yellow-400">24x7</span>
          </h2>
          <p className="text-slate-400 text-xs mt-1 font-semibold uppercase tracking-[0.2em]">
            Secure Admin Access
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {loginStep === "credentials" && (
            <>
              {/* Email Input */}
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                  Admin Identifier
                </label>
                <div className="relative group">
                  <Mail className="absolute left-4 top-3.5 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    type="email"
                    placeholder="admin@suraksha247.com"
                    disabled={isLoading}
                    className={`w-full bg-slate-900/50 border ${errors.email ? "border-red-500" : "border-white/10"} rounded-xl px-12 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder:text-slate-700`}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: "Invalid email format",
                      },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-[10px] mt-1 ml-1">
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                    Secret Key
                  </label>
                </div>
                <div className="relative group">
                  <Lock className="absolute left-4 top-3.5 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    disabled={isLoading}
                    className={`w-full bg-slate-900/50 border ${errors.password ? "border-red-500" : "border-white/10"} rounded-xl px-12 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder:text-slate-700`}
                    {...register("password", {
                      required: "Password is required",
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-3.5 text-slate-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-400 text-[10px] mt-1 ml-1">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </>
          )}

          {loginStep === "otp" && (
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                Email Verification Code
              </label>
              <p className="text-xs text-slate-400 mb-3">
                Enter the six-digit code sent to {pendingEmail}.
              </p>
              <div className="relative group">
                <ShieldCheck className="absolute left-4 top-3.5 w-5 h-5 text-slate-500 group-focus-within:text-blue-400 transition-colors" />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  disabled={isLoading}
                  className={`w-full bg-slate-900/50 border ${errors.otp ? "border-red-500" : "border-white/10"} rounded-xl px-12 py-3.5 text-sm tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder:text-slate-700`}
                  {...register("otp", {
                    required: "Verification code is required",
                    pattern: {
                      value: /^\d{6}$/,
                      message: "Enter the six-digit code",
                    },
                  })}
                />
              </div>
              {errors.otp && (
                <p className="text-red-400 text-[10px] mt-1 ml-1">
                  {errors.otp.message}
                </p>
              )}
              <button
                type="button"
                onClick={() => setLoginStep("credentials")}
                className="text-xs text-blue-400 hover:text-yellow-400 transition-colors"
              >
                Use a different account
              </button>
            </div>
          )}

          {/* Global Error Display */}
          {errors.root && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-lg text-xs text-center animate-shake">
              {errors.root.message}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800/50 text-white py-4 rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-[0.98] mt-4"
          >
            <span className="flex items-center justify-center gap-2">
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {loginStep === "credentials"
                    ? "Verifying credentials..."
                    : "Verifying code..."}
                </>
              ) : (
                <>
                  {loginStep === "credentials"
                    ? "Continue to verification"
                    : "Verify & Sign In"}{" "}
                  <LogIn
                    size={18}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </>
              )}
            </span>
          </button>
        </form>

        {/* Support Link */}
        <div className="mt-8 pt-8 border-t border-white/5 text-center">
          <p className="text-slate-500 text-xs">
            Forgot credentials?{" "}
            <Link
              to="/contact"
              className="text-blue-400 hover:text-yellow-400 font-bold transition-colors"
            >
              Contact Tech Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
